import { Input, Component } from "@angular/core";
import { UserAgent } from "./user-agent";
import { ViewportSize } from "./viewport-size";

interface Source {
    srcset : string;
    sizes? : string;
    type? : string;
    media? : string;
}

@Component({
    selector: 'responsive-picture',
    template: `
        <picture>
            <source *ngFor="let src of sizes" [attr.srcset]="src.srcset || null" [attr.media]="src.media || null" [attr.type]="src.type || null" />
            <img [attr.src]="legacySrc || null" [attr.alt]="alt || null" />
        </picture>
    `,
    styles: [
        `
            :host {
                display: block;
            }

            picture, img {
                width: 100%;
            }
        `
    ]
})
export class ResponsivePictureComponent {
    constructor(
        private userAgent : UserAgent,
        private viewportSize : ViewportSize
    ) {
        
    }

    private _src : string;

    legacySrc : string;

    @Input()
    alt : string;

    @Input()
    get src() : string {
        return this._src;
    }

    set src(value) { 
        this._src = value;
        setTimeout(() => this.setup(value));
    }

    sizes : Source[];

    @Input()
    widthFactor = 1;

    @Input()
    mobileWidthFactor = 1;

    @Input()
    width : number = undefined;

    setup(value : string) {
        if (typeof value === 'string') {
            if (/\/\/images\.contentful/.test(value) || /images\.ctfassets\.net/.test(value)) {

                if (this.width) {
                    // Exact size, specified in markup
                    // Useful if the image is always sized the same way.
                    let url = `${value}?w=${this.width}`;
                    this.sizes = [ { srcset: this.legacySrc }];
                    this.legacySrc = url;
                } else {
                    let f = this.widthFactor;
                    let sizes = [1920.0, 1280.0, 640.0, 320.0];
                    this.sizes = []
                        .concat(sizes.map(px => ({
                            srcset: `${value}?w=${px}&fm=webp`,
                            type: 'image/webp',
                            media: `(min-width: ${px/f}px)`
                        })))
                        .concat(sizes.map(px => ({
                            srcset: `${value}?w=${px}`,
                            media: `(min-width: ${px/f}px)`
                        })))
                    ;
                    this.legacySrc = `${value}?w=${1920.0/f}`;
                }
            } else if (/\/assets\/([^\/]+\.(png|jpg))$/.test(value)) {
                let path : string = value;
                let match = path.match(/\/assets\/([^\/]+\.(png|jpg))$/);
                let f = this.widthFactor;
                
                if (match) {
                    let assetName = match[1];
                    let size = this.viewportSize.selectAssetSize(this.widthFactor, this.mobileWidthFactor);
                    let sizes = {
                        original: 1920,
                        xhd: 1920,
                        hd: 1920,
                        hd720: 1280,
                        sd: 1280,
                        sm: 640,
                        xs: 320
                    };
    
                    let idealWidth = sizes[size] || 1920;
                    let sizeList = [1920, 1280, 640, 320];
                    this.sizes = []
                        .concat(sizeList.map(px => ({
                            srcset: `/assets/generated/${px}px-${assetName}`,
                            media: `(min-width: ${px/f}px)`
                        })))
                    ;
                    this.legacySrc = `/assets/generated/${idealWidth}px-${assetName}`;
                } else {
                    this.legacySrc = path;
                    this.sizes = [ { srcset: this.legacySrc }]
                }
            } else {
                // No dynamic image selection available
                this.legacySrc = value;
                this.sizes = [ { srcset: value }];
            }

        }
    }
}